import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { CalendarCheck2, Undo2, CheckCircle2 } from "lucide-react";
import { API_URL } from "../../lib/utils";

const STATUS = {
  delivered: { label: "Entregue à AGT", cls: "bg-emerald-500/15 text-emerald-700 border-emerald-500/30" },
  exported: { label: "Exportado · falta confirmar", cls: "bg-sky-500/15 text-sky-700 border-sky-500/30" },
  pending: { label: "Por exportar", cls: "bg-amber-500/15 text-amber-700 border-amber-500/30" },
  overdue: { label: "EM ATRASO", cls: "bg-red-600 text-white border-red-600" },
  empty: { label: "Sem documentos", cls: "bg-muted text-muted-foreground" },
};

export const SaftPeriodsCard = ({ onChanged }) => {
  const [periods, setPeriods] = useState([]);
  const [busy, setBusy] = useState("");
  const [refs, setRefs] = useState({});

  const load = () => axios.get(`${API_URL}/saft/periods?limit=12`, { withCredentials: true }).then((r) => setPeriods(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const mark = async (p) => {
    setBusy(p.month);
    try {
      await axios.post(`${API_URL}/saft/periods/${p.month}/delivered`, { reference: refs[p.month] || null }, { withCredentials: true });
      toast.success(`SAF-T ${p.label} marcado como entregue à AGT`);
      await load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Não foi possível confirmar a entrega"); }
    finally { setBusy(""); }
  };

  const unmark = async (p) => {
    setBusy(p.month);
    try {
      await axios.delete(`${API_URL}/saft/periods/${p.month}/delivered`, { withCredentials: true });
      toast.success("Confirmação removida");
      await load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setBusy(""); }
  };

  if (!periods.length) return null;
  const overdue = periods.filter((p) => p.status === "overdue").length;

  return (
    <Card className={overdue ? "border-red-500/40" : ""} data-testid="saft-periods-card">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2"><CalendarCheck2 className="w-5 h-5" /> Entrega mensal à AGT</CardTitle>
        <CardDescription>Prazo: até ao último dia do mês seguinte. Depois de submeter o ficheiro no portal da AGT, confirme aqui a entrega{overdue ? ` — ${overdue} mês(es) em atraso` : ""}.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {periods.map((p) => {
          const s = STATUS[p.status] || STATUS.pending;
          return (
            <div key={p.month} className="flex flex-wrap items-center gap-3 p-2.5 rounded border text-sm" data-testid={`saft-period-${p.month}`}>
              <span className="font-mono font-semibold w-20">{p.label}</span>
              <Badge variant="outline" className={s.cls} data-testid={`saft-period-status-${p.month}`}>{s.label}</Badge>
              <span className="text-muted-foreground">{p.invoices} doc(s) · prazo {p.deadline.split("-").reverse().join("/")}{p.exported_at ? ` · exportado ${p.exported_at.slice(0, 10)}` : ""}</span>
              <div className="ml-auto flex items-center gap-2">
                {p.status === "delivered" ? (
                  <>
                    <span className="text-xs text-emerald-700 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> {p.delivered.delivered_at}{p.delivered.reference ? ` · ref. ${p.delivered.reference}` : ""} · {p.delivered.by_name}</span>
                    <Button size="sm" variant="ghost" disabled={busy === p.month} onClick={() => unmark(p)} data-testid={`saft-period-unmark-${p.month}`}><Undo2 className="w-3.5 h-3.5 mr-1" /> Anular</Button>
                  </>
                ) : p.status === "empty" ? (
                  <span className="text-xs text-muted-foreground">Nada a entregar</span>
                ) : (
                  <>
                    <Input placeholder="Ref.ª / comprovativo (opcional)" value={refs[p.month] || ""} onChange={(e) => setRefs({ ...refs, [p.month]: e.target.value })} className="h-8 w-52 text-xs" data-testid={`saft-period-ref-${p.month}`} />
                    <Button size="sm" disabled={busy === p.month} onClick={() => mark(p)} data-testid={`saft-period-mark-${p.month}`}><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Marcar entregue</Button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};
